import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { goodsReceiptPath } from '../../lib/slug';
import { receivingApi, grnStatusMeta, discrepancyReasonLabel } from './receivingApi';
import '../requisitions/requisitionPrint.css';

const date = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function GoodsReceiptPrintPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { properties } = useProperty();

  const [grn, setGrn] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, us, locs] = await Promise.all([
        receivingApi.get(routeRef),
        catalogApi.listUnits().catch(() => []),
        catalogApi.listLocations().catch(() => []),
      ]);
      setGrn(g);
      setUnits(us || []);
      setLocations(locs || []);
      const act = await receivingApi.listActivity(g.id, { pageSize: 100 }).catch(() => ({ items: [] }));
      setActivity(act?.items || []);
    } catch (err) {
      setError(err?.message || t('Could not load goods receipt.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;
  const property = properties.find((p) => p.id === grn?.propertyId);

  if (loading) {
    return <div className="ps-scope rq-print__loading"><Spinner size={26} /></div>;
  }
  if (error || !grn) {
    return (
      <div className="ps-scope rq-print__loading">
        <p>{error || t('Not found.')}</p>
        <button type="button" className="rq-print__btn" onClick={() => navigate('/receiving')}>{t('Back')}</button>
      </div>
    );
  }

  const statusMeta = grnStatusMeta(grn.status);
  const lines = grn.lines || [];
  const anyRejected = lines.some((l) => Number(l.rejectedQuantity) > 0);
  const anyPriceVariance = lines.some((l) => l.hasPriceVariance);
  const totalAccepted = lines.reduce((a, l) => a + Number(l.receivedQuantity || 0), 0);
  const totalRejected = lines.reduce((a, l) => a + Number(l.rejectedQuantity || 0), 0);
  const lineValue = (l) =>
    (l.unitPrice ? `${l.unitPrice.currency} ${formatAmount(Number(l.unitPrice.amount) * Number(l.receivedQuantity || 0))}` : '—');
  const history = [...activity].reverse();
  const colSpan = 5 + (anyRejected ? 2 : 0) + (anyPriceVariance ? 1 : 0);

  return (
    <div className="ps-scope rq-print">
      <div className="rq-print__toolbar">
        <button type="button" className="rq-print__btn rq-print__btn--ghost" onClick={() => navigate(goodsReceiptPath(grn))}>
          <Icon.ArrowLeft size={15} /> {t('Back')}
        </button>
        <button type="button" className="rq-print__btn" onClick={() => window.print()}>
          <Icon.Printer size={15} /> {t('Print / Save as PDF')}
        </button>
      </div>

      <div className="rq-doc" role="document">
        <header className="rq-doc__head">
          <div className="rq-doc__brand">
            <div className="rq-doc__property">{property?.name || t('Property')}</div>
            <div className="rq-doc__subtitle">{t('Goods Receipt Note')}</div>
          </div>
          <div className="rq-doc__ref">
            <div className="rq-doc__number">{grn.number || t('Draft')}</div>
            <div className={`rq-doc__status rq-doc__status--${statusMeta.tone}`}>{t(statusMeta.label)}</div>
          </div>
        </header>

        <h1 className="rq-doc__title">
          {t('Goods received against {number}', { number: grn.purchaseOrderNumber || '—' })}
        </h1>

        <section className="rq-doc__meta">
          <div><span>{t('Supplier')}</span><strong>{grn.supplierName || '—'}</strong></div>
          <div><span>{t('Purchase order')}</span><strong>{grn.purchaseOrderNumber || '—'}</strong></div>
          <div><span>{t('Received date')}</span><strong>{date(grn.receivedDate)}</strong></div>
          <div><span>{t('Received into')}</span><strong>{locationName(grn.locationId) || '—'}</strong></div>
          <div><span>{t('Received by')}</span><strong>{grn.receivedByName || '—'}</strong></div>
          <div><span>{t('Delivery note')}</span><strong>{grn.deliveryNote || '—'}</strong></div>
        </section>

        <table className="rq-doc__lines">
          <thead>
            <tr>
              <th className="rq-doc__c-num">#</th>
              <th>{t('Description')}</th>
              <th className="rq-doc__c-qty">{t('Ordered')}</th>
              <th className="rq-doc__c-qty">{t('Received')}</th>
              {anyRejected && <th className="rq-doc__c-qty">{t('Rejected')}</th>}
              {anyRejected && <th>{t('Reason')}</th>}
              <th className="rq-doc__c-unit">{t('Unit')}</th>
              <th className="rq-doc__c-money">{t('Unit price')}</th>
              {anyPriceVariance && <th className="rq-doc__c-money">{t('PO price')}</th>}
              <th className="rq-doc__c-money">{t('Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.lineId || i}>
                <td className="rq-doc__c-num">{i + 1}</td>
                <td>
                  <div className="rq-doc__desc">{l.description}</div>
                  {l.sku && <div className="rq-doc__sku">{l.sku}</div>}
                </td>
                <td className="rq-doc__c-qty">{l.orderedQuantity}</td>
                <td className="rq-doc__c-qty">{l.receivedQuantity}</td>
                {anyRejected && <td className="rq-doc__c-qty">{Number(l.rejectedQuantity) > 0 ? l.rejectedQuantity : '—'}</td>}
                {anyRejected && <td>{discrepancyReasonLabel(l.discrepancyReason) ? t(discrepancyReasonLabel(l.discrepancyReason)) : '—'}</td>}
                <td className="rq-doc__c-unit">{uomCode(l.uomId) || '—'}</td>
                <td className="rq-doc__c-money">{money(l.unitPrice)}</td>
                {anyPriceVariance && <td className="rq-doc__c-money">{l.hasPriceVariance ? money(l.poUnitPrice) : '—'}</td>}
                <td className="rq-doc__c-money">{lineValue(l)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={colSpan} className="rq-doc__total-label" style={{ fontWeight: 'normal' }}>
                {t('Accepted {accepted} · Rejected {rejected}', { accepted: totalAccepted, rejected: totalRejected })}
              </td>
              <td className="rq-doc__c-money" />
            </tr>
            <tr>
              <td colSpan={colSpan} className="rq-doc__total-label">{t('Received value')}</td>
              <td className="rq-doc__c-money rq-doc__total-value">{money(grn.total)}</td>
            </tr>
          </tfoot>
        </table>

        {grn.notes && (
          <section className="rq-doc__notes">
            <span className="rq-doc__section-label">{t('Notes')}</span>
            <p>{grn.notes}</p>
          </section>
        )}

        {grn.discrepancyStatus === 'Resolved' && grn.resolutionNotes && (
          <section className="rq-doc__notes">
            <span className="rq-doc__section-label">{t('Discrepancy resolution')}</span>
            <p>{grn.resolutionNotes}{grn.resolvedByName ? ` — ${grn.resolvedByName}` : ''}</p>
          </section>
        )}

        <section className="rq-doc__sign">
          <div className="rq-doc__sign-block">
            <span className="rq-doc__section-label">{t('Received by')}</span>
            <div className="rq-doc__sign-mark" />
            <div className="rq-doc__sign-name">{grn.receivedByName || '—'}</div>
            <div className="rq-doc__sign-meta">{dateTime(grn.receivedDate)}</div>
          </div>
        </section>

        {history.length > 0 && (
          <section className="rq-doc__history">
            <span className="rq-doc__section-label">{t('Audit trail')}</span>
            <ul>
              {history.map((a) => (
                <li key={a.id}>
                  <span className="rq-doc__history-date">{dateTime(a.createdAt)}</span>
                  <span className="rq-doc__history-text">{`${a.actorName || t('Someone')} ${a.summary}`}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="rq-doc__footer">
          <span>{grn.number || t('Draft')} · {property?.name || ''}</span>
          <span>{t('Generated {timestamp}', { timestamp: dateTime(new Date()) })}</span>
        </footer>
      </div>
    </div>
  );
}
